// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import NotificationType from "./notification-type.js"
import User from "./user.js"

const modelClassData = {
  "attributes": {
    "action": {
      "column": null,
      "name": "action",
      "selected_by_default": null,
      "translated": null
    },
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "notification_type_id": {
      "column": null,
      "name": "notification_type_id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "Notification",
  "collectionKey": "notifications",
  "collectionName": "notifications",
  "i18nKey": "notification",
  "camelizedLower": "notification",
  "name": "Notification",
  "nameDasherized": "notification",
  "pluralName": "notifications",
  "ransackable_associations": [
    {
      "className": "NotificationTypeAction",
      "collectionName": "notification_type_actions",
      "foreignKey": "action_id",
      "name": "action",
      "macro": "belongs_to",
      "resource_name": "NotificationTypeAction",
      "through": null
    },
    {
      "className": "Audit",
      "collectionName": null,
      "foreignKey": "audit_id",
      "name": "audit",
      "macro": "belongs_to",
      "resource_name": null,
      "through": null
    },
    {
      "className": "Notification::Audit",
      "collectionName": "notification_audits",
      "foreignKey": "notification_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "NotificationAudit",
      "through": null
    },
    {
      "className": "Notification::Audit",
      "collectionName": "notification_audits",
      "foreignKey": "notification_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "NotificationAudit",
      "through": null
    },
    {
      "className": "EmailTemplate",
      "collectionName": "email_templates",
      "foreignKey": "email_template_id",
      "name": "email_template",
      "macro": "has_one",
      "resource_name": "EmailTemplate",
      "through": "action"
    },
    {
      "className": "NotificationType",
      "collectionName": "notification_types",
      "foreignKey": "notification_type_id",
      "name": "notification_type",
      "macro": "has_one",
      "resource_name": "NotificationType",
      "through": "action"
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "action_id",
      "column": {
        "default": null,
        "name": "action_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "audit_id",
      "column": {
        "default": null,
        "name": "audit_id",
        "null": true,
        "type": null
      }
    },
    {
      "name": "audit_type",
      "column": {
        "default": null,
        "name": "audit_type",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "email",
      "column": {
        "default": null,
        "name": "email",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "extra_liquid_variables",
      "column": {
        "default": null,
        "name": "extra_liquid_variables",
        "null": true,
        "type": "text"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "locale",
      "column": {
        "default": null,
        "name": "locale",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "user_id",
      "column": {
        "default": null,
        "name": "user_id",
        "null": true,
        "type": null
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "NotificationType",
      "collectionName": "notification_types",
      "foreignKey": "notification_type_id",
      "name": "notification_type",
      "macro": "has_one",
      "resource_name": "NotificationType",
      "through": "action"
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "paramKey": "notification",
  "primaryKey": "id"
}

/** Frontend model for Notification. */
export default class Notification extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {any} */
  action() {
    return this.readAttributeUnderscore("action")
  }

  /** @returns {boolean} */
  hasAction() {
    const value = this.action()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  notificationTypeId() {
    return this.readAttributeUnderscore("notification_type_id")
  }

  /** @returns {boolean} */
  hasNotificationTypeId() {
    const value = this.notificationTypeId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./notification-type.js").default | null} */
  notificationType() {
    return this._readHasOneReflection({modelClass: NotificationType, reflectionName: "notification_type"})
  }

  /** @returns {Promise<import("./notification-type.js").default | null>} */
  loadNotificationType() {
    if (!("id" in this)) throw new Error("Primary key method wasn't defined: id")

    const id = this.id()

    return this._loadHasOneReflection(
      {
        reflectionName: "notification_type",
        model: this,
        modelClass: NotificationType
      },
      {
        params: {
          through: {
            model: "Notification",
            id,
            reflection: "notification_type"
          }
        }
      }
    )
  }

  /** @returns {Promise<import("./notification-type.js").default | null>} */
  ensureNotificationTypeLoaded() {
    return this.ensureAssociationLoaded("notification_type")
  }

  /** @returns {import("./user.js").default | null} */
  user() {
    return this._readBelongsToReflection({modelClass: User, reflectionName: "user"})
  }

  /** @returns {Promise<import("./user.js").default | null>} */
  loadUser() {
    if (!("userId" in this)) throw new Error("Foreign key method wasn't defined: userId")

    const id = this.userId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "user",
        model: this,
        modelClass: User
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./user.js").default | null>} */
  ensureUserLoaded() {
    return this.ensureAssociationLoaded("user")
  }
}
